/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { Divider, Grid, ShowMoreText, breakpoints, useMediaQuery } from '@coursera/cds-core';

import SectionHeadingComponent from 'bundles/browse-common/components/SectionHeadingComponent';
import ValuePropositionItem from 'bundles/browse-common/components/ValuePropositionItem';
import ValuePropositionItemPlaceholder from 'bundles/browse-common/components/ValuePropositionItemPlaceholder';
import { getDisplayText, getJustifyContent, getNumberAndLabel } from 'bundles/browse-common/utils/pageConfigUtils';

import _t from 'i18n!nls/browse-common';

export type AdditionalContent = {
  text?: string;
  value?: number;
  category?: string;
  icon?: React.ReactElement;
};

type Props = {
  title: string;
  description?: string | null;
  additionalContent?: AdditionalContent[] | null;
  showPageTitleHeadingSize?: boolean;
  isLoading?: boolean;
};

const styles = {
  icon: css`
    margin-bottom: -2px;
  `,
  additionalContent: css`
    ${breakpoints.up('md')} {
      gap: var(--cds-spacing-300);
    }
  `,
  smallScreenItem: css`
    margin-right: var(--cds-spacing-50);
  `,
  smallScreenAdditionalContent: css`
    margin-top: var(--cds-spacing-200);
  `,
  iconLessItem: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
  `,
  iconLessItemSmallScreen: css`
    display: flex;
    flex-direction: row;
    gap: var(--cds-spacing-50);
  `,
  dividerSpacing: css`
    margin-left: var(--cds-spacing-200);
    margin-right: var(--cds-spacing-200);
  `,
  description: css`
    & .cds-ShowMoreText-content {
      color: var(--cds-color-neutral-primary-weak);
    }
  `,
};

const ValuePropositionComponent = (props: Props) => {
  const { title, description, additionalContent, showPageTitleHeadingSize = false, isLoading = false } = props;
  const isSmallScreen = useMediaQuery(breakpoints.down('sm'));
  const hasAdditionalContent = additionalContent && additionalContent.length > 0;
  const hasIcons = additionalContent?.every((content) => content.icon) ?? false;
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  return (
    <Grid container spacing={12}>
      <Grid item xs={12} md={hasAdditionalContent ? 6 : 12}>
        <SectionHeadingComponent
          showPageTitleHeadingSize={showPageTitleHeadingSize}
          dataTestId={showPageTitleHeadingSize ? 'page-title-heading' : undefined}
        >
          {title}
        </SectionHeadingComponent>
        {description && (
          <ShowMoreText
            collapsedNumLines={3}
            css={styles.description}
            aria-label={
              descriptionExpanded
                ? _t('Show less information about #{title}', { title })
                : _t('Show more information about #{title}', { title })
            }
            onChange={setDescriptionExpanded}
          >
            {description}
          </ShowMoreText>
        )}
      </Grid>
      {hasAdditionalContent && (
        <Grid
          container
          item
          xs={12}
          md={6}
          css={[styles.additionalContent, description && isSmallScreen && styles.smallScreenAdditionalContent]}
          alignContent="center"
          justifyContent={getJustifyContent(isSmallScreen, hasIcons)}
          direction="row"
        >
          {additionalContent.map((content, index) => {
            const hasMoreItems = index < additionalContent.length - 1;
            const displayText: string = getDisplayText(content);
            const { number, label } = getNumberAndLabel(content);

            if (isLoading) {
              return (
                <React.Fragment key={`shimmer-${content.text || content.value}`}>
                  <div css={isSmallScreen ? styles.iconLessItemSmallScreen : styles.iconLessItem}>
                    <ValuePropositionItemPlaceholder isSmallScreenLayout={isSmallScreen} />
                  </div>
                  {hasMoreItems && <Divider color="light" orientation="vertical" css={styles.dividerSpacing} />}
                </React.Fragment>
              );
            }
            return (
              <ValuePropositionItem
                key={`${content.text || content.value || index}`}
                content={content}
                displayText={displayText || ''}
                number={number}
                label={label}
                hasMoreItems={hasMoreItems}
                isSmallScreen={isSmallScreen}
                hasIcons={hasIcons}
              />
            );
          })}
        </Grid>
      )}
    </Grid>
  );
};

export default ValuePropositionComponent;
